import { format } from 'date-fns'
import { ArrowDownToLine, Calendar, Folder, Loader } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { use } from 'react'
import Markdown from 'react-markdown'
import { useMatch, useParams } from 'react-router'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { H2, H3, H4, P, Small } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TEMPLATE_CATEGORIES } from '@/config/templates'
import {
  useCompanyPath,
  useIdCompany,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { TemplateAppCard } from '@/routes/template/app-card'
import { TemplateDbCard } from '@/routes/template/db-card'
import { TEMPLATE_QUERY } from '@/routes/template/gql'
import * as Styled from '@/routes/template/styles'
import { DeployTemplateSheet } from '@/routes/templates/deploy-template-sheet'
import type {
  TemplateQuery,
  TemplateQueryVariables,
} from '@/routes/template/gql.types.gen'

export const TemplatePage = () => {
  const { rbac } = useRBAC()
  const { navigateToPath } = useCompanyPath()
  const { idCompany } = useIdCompany()
  const { name = '' } = useParams()
  const { isSuspended } = use(CompanySuspensionContext)
  const [savedTemplateInstance, setTemplateInstance] = useState<any | null>(
    null
  )
  const isDeploySheetOpen = Boolean(useMatch('/template/:name/deploy'))

  const { data } = useSuspenseQuery<TemplateQuery, TemplateQueryVariables>(
    TEMPLATE_QUERY,
    {
      variables: { name, idCompany },
    }
  )

  useEffect(() => {
    rbac.company(idCompany).apps.create.ensure()
    rbac.company(idCompany).databases.create.ensure()
  }, [rbac, idCompany])

  const template = data?.templateByName

  const inProgressTemplateInstance = useMemo(() => {
    return template?.instances.find(
      (instance) => instance.status === 'inProgress'
    )
  }, [template?.instances])

  const successTemplateInstance = useMemo(() => {
    return template?.instances.find((instance) => instance.status === 'success')
  }, [template?.instances])

  useEffect(() => {
    if (inProgressTemplateInstance) {
      setTemplateInstance(inProgressTemplateInstance)
    } else if (successTemplateInstance?.id === savedTemplateInstance?.id) {
      setTemplateInstance(successTemplateInstance)
    }
  }, [
    inProgressTemplateInstance,
    successTemplateInstance,
    savedTemplateInstance,
  ])

  const templateInstance =
    inProgressTemplateInstance || savedTemplateInstance || null

  const isDeployDisabled =
    Boolean(savedTemplateInstance) ||
    Boolean(inProgressTemplateInstance) ||
    isSuspended

  return (
    <>
      <Styled.Grid>
        <Styled.Header>
          <img
            src={template?.logoUrl ?? ''}
            alt={`${template?.displayName} logo`}
            className={cn('max-h-[32px] w-auto', {
              'dark:invert': template?.displayName === 'Ghost',
            })}
          />
          <H2 title={template?.displayName ?? ''}>{template?.displayName}</H2>
          <Button
            className="ml-auto"
            onClick={() =>
              navigateToPath({
                pathname: '/template/:name/deploy',
                variables: { name },
                options: { replace: true },
              })
            }
            disabled={isDeployDisabled}
          >
            {inProgressTemplateInstance ? (
              <>
                <Loader className="animate-spin" />
                <span>Deploying...</span>
              </>
            ) : (
              'Deploy template'
            )}
          </Button>
        </Styled.Header>
        <Styled.GridColumn>
          <Styled.DottedCanvas>
            {template?.services.map((service) => {
              if (
                ['applicationDockerImage', 'applicationGitPublic'].includes(
                  service.serviceType
                )
              ) {
                return (
                  <TemplateAppCard
                    key={service.id}
                    templateInstance={templateInstance}
                    service={service}
                  />
                )
              }
              return (
                <TemplateDbCard
                  key={service.id}
                  templateInstance={templateInstance}
                  service={service}
                />
              )
            })}
          </Styled.DottedCanvas>
          <Card>
            <CardHeader>
              <CardTitle>
                <H3>README</H3>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <Markdown
                components={{
                  a: ({ children, ...props }) => (
                    <a
                      {...props}
                      target="_blank"
                      className="text-primary underline"
                    >
                      {children}
                    </a>
                  ),
                  h1: ({ children }) => <H2 className="mt-5">{children}</H2>,
                  h2: ({ children }) => <H3 className="mt-5">{children}</H3>,
                  h3: ({ children }) => <H4 className="mt-5">{children}</H4>,
                  p: ({ children }) => <P className="text-sm">{children}</P>,
                  ol: ({ children }) => (
                    <ol className="list-decimal ml-6">{children}</ol>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc ml-6">{children}</ul>
                  ),
                  li: ({ children }) => <li className="mt-1">{children}</li>,
                }}
              >
                {template?.readme ?? ''}
              </Markdown>
            </CardContent>
          </Card>
        </Styled.GridColumn>
        <Styled.GridColumn>
          {template ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  <H3>Metadata</H3>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Styled.MetadataGrid>
                  <Calendar className="size-4 text-muted-foreground" />
                  <Small>Created at {format(template.createdAt, 'PPP')}</Small>
                  <Folder className="size-4 text-muted-foreground" />
                  <Small>
                    {
                      TEMPLATE_CATEGORIES[
                        (template.categorySlug ??
                          '') as keyof typeof TEMPLATE_CATEGORIES
                      ]
                    }
                  </Small>
                  <ArrowDownToLine className="size-4 text-muted-foreground" />
                  <Small>
                    {template.deploymentCount > 1
                      ? `${template.deploymentCount} downloads`
                      : `${template.deploymentCount} download`}
                  </Small>
                </Styled.MetadataGrid>
              </CardContent>
            </Card>
          ) : null}
          {template?.requirements ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  <H3>Requirements</H3>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <Markdown>{template.requirements ?? ''}</Markdown>
              </CardContent>
            </Card>
          ) : null}
        </Styled.GridColumn>
      </Styled.Grid>
      <DeployTemplateSheet
        isOpen={isDeploySheetOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/template/:name',
            variables: { name },
            options: { replace: true },
          })
        }
      />
    </>
  )
}
